import type { RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/features/auth/stores/authStore';

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    // Decide at navigation time so refreshing the root URL takes the user
    // straight to /login when there's no token (no flash of /overview).
    redirect: () => {
      const auth = useAuthStore();
      return auth.isAuthenticated ? { name: 'overview' } : { name: 'auth.login' };
    },
  },
  {
    path: '/login',
    name: 'auth.login',
    component: () => import('@/shared/layout/AuthLayout.vue'),
    meta: { requiresAuth: false, title: 'Sign in' },
    children: [
      {
        path: '',
        name: 'auth.login.index',
        component: () => import('@/features/auth/pages/LoginPage.vue'),
      },
    ],
  },
  {
    path: '/verify',
    component: () => import('@/shared/layout/AuthLayout.vue'),
    meta: { requiresAuth: false, title: 'Verify' },
    children: [
      {
        path: '',
        name: 'auth.verify',
        component: () => import('@/features/auth/pages/VerifyOtpPage.vue'),
      },
    ],
  },
  {
    path: '/unauthorized',
    component: () => import('@/shared/layout/AuthLayout.vue'),
    meta: { requiresAuth: true, title: 'Unauthorized' },
    children: [
      {
        path: '',
        name: 'auth.unauthorized',
        component: () => import('@/features/auth/pages/UnauthorizedPage.vue'),
      },
    ],
  },
  {
    path: '/',
    component: () => import('@/shared/layout/AdminLayout.vue'),
    meta: { requiresAuth: true, requiresTenantAdmin: true },
    children: [
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/features/dashboard/pages/DashboardPage.vue'),
        meta: { title: 'Dashboard' },
      },
      {
        path: 'dashboard/leagues/:id',
        name: 'dashboard.league',
        component: () => import('@/features/dashboard/pages/LeaguePerformancePage.vue'),
        meta: { title: 'League performance' },
        props: true,
      },
      {
        path: 'dashboard/challenges/:id',
        name: 'dashboard.challenge',
        component: () => import('@/features/dashboard/pages/ChallengePerformancePage.vue'),
        meta: { title: 'Challenge performance' },
        props: true,
      },
      {
        path: 'overview',
        name: 'overview',
        component: () => import('@/features/overview/pages/OverviewPage.vue'),
        meta: { title: 'Overview' },
      },
      {
        path: 'admins',
        name: 'admins',
        component: () => import('@/features/admins/pages/AdminsPage.vue'),
        meta: { title: 'Tenant admins' },
      },
      {
        path: 'users',
        name: 'users',
        component: () => import('@/features/users/pages/UsersPage.vue'),
        meta: { title: 'Users' },
      },
      {
        path: 'users/:userId',
        name: 'users.detail',
        component: () => import('@/features/users/pages/UserDetailPage.vue'),
        meta: { title: 'User detail' },
        props: true,
      },
      {
        path: 'users/:userId/history',
        name: 'users.history',
        component: () => import('@/features/users/pages/UserHistoryPage.vue'),
        meta: { title: 'User history' },
        props: true,
      },
      {
        path: 'challenges',
        name: 'challenges',
        component: () => import('@/features/challenges/pages/ChallengesPage.vue'),
        meta: { title: 'Challenges' },
      },
      {
        path: 'challenges/new',
        name: 'challenges.new',
        component: () => import('@/features/challenges/pages/ChallengeEditPage.vue'),
        meta: { title: 'New challenge' },
      },
      {
        path: 'challenges/:id',
        name: 'challenges.detail',
        component: () => import('@/features/challenges/pages/ChallengeEditPage.vue'),
        meta: { title: 'Challenge' },
        props: true,
      },
      {
        path: 'submissions',
        name: 'submissions',
        component: () => import('@/features/submission-audit/pages/SubmissionsListPage.vue'),
        meta: { title: 'Submissions' },
      },
      {
        path: 'submissions/:id',
        name: 'submissions.audit',
        component: () => import('@/features/submission-audit/pages/SubmissionAuditPage.vue'),
        meta: { title: 'Submission audit' },
        props: true,
      },
      {
        path: 'leagues',
        name: 'leagues',
        component: () => import('@/features/leagues/pages/LeaguesPage.vue'),
        meta: { title: 'Leagues' },
      },
      {
        path: 'leagues/new',
        name: 'leagues.new',
        component: () => import('@/features/leagues/pages/LeagueEditPage.vue'),
        meta: { title: 'New league' },
      },
      {
        path: 'leagues/:id',
        name: 'leagues.detail',
        component: () => import('@/features/leagues/pages/LeagueEditPage.vue'),
        meta: { title: 'League' },
        props: true,
      },
      {
        path: 'quizzes',
        name: 'quizzes',
        component: () => import('@/features/quizzes/pages/QuizzesPage.vue'),
        meta: { title: 'Quizzes' },
      },
      {
        path: 'quizzes/new',
        name: 'quizzes.new',
        component: () => import('@/features/quizzes/pages/QuizEditPage.vue'),
        meta: { title: 'New quiz' },
      },
      {
        path: 'quizzes/:id',
        name: 'quizzes.detail',
        component: () => import('@/features/quizzes/pages/QuizEditPage.vue'),
        meta: { title: 'Quiz' },
        props: true,
      },
      {
        path: 'polls',
        name: 'polls',
        component: () => import('@/features/polls/pages/PollsPage.vue'),
        meta: { title: 'Polls' },
      },
      {
        path: 'polls/new',
        name: 'polls.new',
        component: () => import('@/features/polls/pages/PollEditPage.vue'),
        meta: { title: 'New poll' },
      },
      {
        path: 'polls/:id',
        name: 'polls.detail',
        component: () => import('@/features/polls/pages/PollEditPage.vue'),
        meta: { title: 'Poll' },
        props: true,
      },
      {
        path: 'plans',
        name: 'plans',
        component: () => import('@/features/plans/pages/PlansPage.vue'),
        meta: { title: 'Plans' },
      },
      {
        path: 'subscriptions',
        name: 'subscriptions',
        component: () => import('@/features/subscriptions/pages/SubscriptionsPage.vue'),
        meta: { title: 'Subscriptions' },
      },
      {
        path: 'purchases',
        name: 'purchases',
        component: () => import('@/features/purchases/pages/PurchasesPage.vue'),
        meta: { title: 'Purchases' },
      },
      {
        path: 'sponsors',
        name: 'sponsors',
        component: () => import('@/features/sponsors/pages/SponsorsPage.vue'),
        meta: { title: 'Sponsors' },
      },
      {
        path: 'sponsors/new',
        name: 'sponsors.new',
        component: () => import('@/features/sponsors/pages/SponsorEditPage.vue'),
        meta: { title: 'New sponsor' },
      },
      {
        path: 'sponsors/:id',
        name: 'sponsors.detail',
        component: () => import('@/features/sponsors/pages/SponsorEditPage.vue'),
        meta: { title: 'Sponsor' },
        props: true,
      },
      {
        path: 'notifications',
        name: 'notifications',
        component: () => import('@/features/notifications/pages/NotificationsPage.vue'),
        meta: { title: 'Notifications' },
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/features/settings/pages/SettingsPage.vue'),
        meta: { title: 'Settings' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: () => {
      const auth = useAuthStore();
      return auth.isAuthenticated ? { name: 'overview' } : { name: 'auth.login' };
    },
  },
];
